import { useMemo, useState } from 'react'
import { useTranslation } from '@/i18n'
import { MoreVertical } from 'lucide-react'
import { Checkbox } from '@/components/ui/checkbox'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { IntuiAvatar } from '@/components/intueri/IntuiAvatar/IntuiAvatar'
import type { Interview, Expert } from '@/service/api'

interface Props {
  interview: Interview
}

type SessionStatus = 'completed' | 'pending' | 'analyzing'

/**
 * UI-20 (Pegah): "Style of buttons is different in each page, must be
 * consistent everywhere". Same pill as the topic list's, and the same removal
 * — no ring, no dot; the tint carries the state.
 */
const STATUS_BADGE: Record<
  SessionStatus,
  { className: string; labelKey: string; defaultLabel: string }
> = {
  completed: {
    className: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300',
    labelKey: 'hek_topic:interviews.status.completed',
    defaultLabel: 'Completed',
  },
  pending: {
    className: 'bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300',
    labelKey: 'hek_topic:interviews.status.pending',
    defaultLabel: 'Pending',
  },
  analyzing: {
    className: 'bg-muted text-muted-foreground',
    labelKey: 'hek_topic:interviews.status.analyzing',
    defaultLabel: 'Analyzing',
  },
}

// Backend gives a single status for the whole interview; reuse it for every
// expert row until per-session status lands on the Interview model.
const mapStatus = (raw: string): SessionStatus => {
  const upper = (raw || '').toUpperCase()
  if (upper === 'COMPLETED') return 'completed'
  if (upper === 'ANALYZING') return 'analyzing'
  return 'pending'
}

const initialsFor = (expert: Expert): string => {
  const source = (expert.name || expert.email || '').trim()
  if (!source) return '?'
  const parts = source.split(/\s+/).filter(Boolean)
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
  }
  return source.slice(0, 2).toUpperCase()
}

const formatDateTime = (iso: string) => {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleString('en-US', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  })
}

const InterviewsTab = ({ interview }: Props) => {
  const { t } = useTranslation()
  const experts = useMemo(() => interview.experts ?? [], [interview.experts])
  const [selected, setSelected] = useState<Set<string>>(new Set())

  const status = mapStatus(interview.status)
  const dateLabel = formatDateTime(interview.interview_start_date)

  const allSelected = experts.length > 0 && experts.every((e) => selected.has(e.email))
  const someSelected = experts.some((e) => selected.has(e.email)) && !allSelected

  const toggleAll = () => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (allSelected) experts.forEach((e) => next.delete(e.email))
      else experts.forEach((e) => next.add(e.email))
      return next
    })
  }

  const toggleRow = (email: string) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(email)) next.delete(email)
      else next.add(email)
      return next
    })
  }

  if (experts.length === 0) {
    return (
      <p className="text-sm text-muted-foreground italic">
        {t('hek_topic:interviews.empty', {
          defaultValue: 'No experts have been invited to this topic yet.',
        })}
      </p>
    )
  }

  const badge = STATUS_BADGE[status]

  return (
    <div className="rounded-lg border border-border overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-background">
          <tr className="border-b border-border text-left text-xs font-medium text-muted-foreground">
            <th className="w-10 px-4 py-3">
              <Checkbox
                checked={allSelected ? true : someSelected ? 'indeterminate' : false}
                onCheckedChange={toggleAll}
                aria-label={t('hek_topic:interviews.select_all', { defaultValue: 'Select all' })}
              />
            </th>
            <th className="px-3 py-3 font-medium">
              {t('hek_topic:interviews.col.name', { defaultValue: 'Name' })}
            </th>
            <th className="px-3 py-3 font-medium">
              {t('hek_topic:interviews.col.role', { defaultValue: 'Role' })}
            </th>
            <th className="px-3 py-3 font-medium">
              {t('hek_topic:interviews.col.description', { defaultValue: 'Description' })}
            </th>
            <th className="px-3 py-3 font-medium">
              {t('hek_topic:interviews.col.date', { defaultValue: 'Date' })}
            </th>
            <th className="px-3 py-3 font-medium">
              {t('hek_topic:interviews.col.status', { defaultValue: 'Status' })}
            </th>
            <th className="w-12 px-3 py-3" />
          </tr>
        </thead>
        <tbody>
          {experts.map((expert) => {
            const isSelected = selected.has(expert.email)
            const name = expert.name?.trim() || expert.email
            return (
              <tr key={expert.email} className="border-t border-border hover:bg-muted/40">
                <td className="px-4 py-3">
                  <Checkbox
                    checked={isSelected}
                    onCheckedChange={() => toggleRow(expert.email)}
                    aria-label={t('hek_topic:interviews.select_row', { defaultValue: 'Select row' })}
                  />
                </td>
                <td className="px-3 py-3">
                  <div className="flex items-center gap-2.5">
                    <IntuiAvatar size="md" fallback={initialsFor(expert)} alt={name} />
                    <span className="font-medium text-foreground">{name}</span>
                  </div>
                </td>
                <td className="px-3 py-3 text-foreground">{expert.role?.trim() || '—'}</td>
                <td className="px-3 py-3 text-muted-foreground max-w-[260px] truncate">—</td>
                <td className="px-3 py-3 text-foreground tabular-nums">{dateLabel}</td>
                <td className="px-3 py-3 align-middle">
                  <span
                    className={`inline-flex min-w-[104px] items-center justify-center rounded-full px-2.5 py-1 text-xs font-medium leading-none whitespace-nowrap ${badge.className}`}
                  >
                    {t(badge.labelKey, { defaultValue: badge.defaultLabel })}
                  </span>
                </td>
                <td className="w-12 px-3 py-3 text-right">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button
                        type="button"
                        className="inline-flex size-7 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
                        aria-label={t('hek_topic:interviews.row_actions', { defaultValue: 'Row actions' })}
                      >
                        <MoreVertical className="size-4" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem>
                        {t('hek_topic:interviews.actions.view', { defaultValue: 'View interview' })}
                      </DropdownMenuItem>
                      <DropdownMenuItem>
                        {t('hek_topic:interviews.actions.resend', { defaultValue: 'Resend invite' })}
                      </DropdownMenuItem>
                      <DropdownMenuItem className="text-destructive focus:text-destructive">
                        {t('hek_topic:interviews.actions.remove', { defaultValue: 'Remove expert' })}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

export default InterviewsTab
